import { getLegacyTokens } from '../authentication/get-legacy-tokens';
import { UserSessionAccessor } from '../authentication/user-provider';
import { getEndToEndUserToken } from '../end-to-end/get-end-to-end-user-tokens';

import { ACCESS_TOKEN_HEADER, ID_TOKEN_HEADER } from '../config/cognito-config';
import { isProd } from '../config/config';

export const apiHost = () => {
  if (isProd) {
    return 'https://employer-api.teamengine.io';
  }
  return 'https://local.employer.expeditiondata.com:8043';
};

export type RequestOptions = RequestInit & {
  /**
   * Whether or not we're uploading a multipart file. We set the Content-Type header by default,
   * and for multipart uploads, the Content-Type header includes a bunch of auto-generated information
   * about the boundaries of the request. The fetch function needs the Content-Type to specifically be
   * unset for this to work properly, so we pass in this flag to let us know not to use the default
   */
  isMultipartUpload?: boolean;

  apiHost?: string;
};

/**
 * Make a request using fetch. If there are valid Cognito tokens in local storage, use them, but otherwise
 * use the Auth0 `getAccessTokenSilently` method to fetch new tokens. This method will throw an exception
 * if it is used outside of a `UserProvider` context or the user is not authenticated.
 *
 * This method will usually only make the request that we ask it to, but if the Auth0 access token has
 * expired, the `getAccessTokenSilently` will call off to Auth0 to refresh it.
 */
export const request = async (
  uri: string,
  method: string,
  opts: RequestOptions,
): Promise<Response> => {
  const options = opts || {};

  const legacyTokens = getLegacyTokens();
  const endToEndUserToken = getEndToEndUserToken();

  /**
   * Depending on if the request is being made by a user with Auth0 tokens, end-to-end user tokens, or
   * Cognito tokens (very unlikely, at this point) - we have to set different headers. This object is spread
   * below in the `headers` variable that is passed into the request
   */
  let tokenHeaders;

  if (endToEndUserToken) {
    tokenHeaders = {
      'x-endToEnd-Authorization': endToEndUserToken,
    };
  } else if (!legacyTokens) {
    const auth0Session = UserSessionAccessor.getSession();
    // If someone tries to use api.ts outside of a UserProvider context, this will be false
    // For now this is fine, but if we ever have public facing pages in Employer, we'll need
    // to rethink this
    if (!auth0Session) {
      throw new Error('Api.ts is being used outside of the user provider context');
    }
    const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = auth0Session;
    if (!isAuthenticated) {
      loginWithRedirect();
    }
    try {
      const token = await getAccessTokenSilently();
      tokenHeaders = {
        Authorization: `Bearer ${token}`,
      };
    } catch (e: any) {
      // We could fail for any of the reasons listed here: https://openid.net/specs/openid-connect-core-1_0.html#AuthError
      // Most of the time, this is going to fail because the user has to log in again, so we'll redirect the user to log in
      // Code lifted from: https://github.com/auth0/auth0-react/blob/master/EXAMPLES.md#4-create-a-useapi-hook-for-accessing-protected-apis-with-an-access-token
      if (e.error === 'login_required') {
        loginWithRedirect();
      }
      if (e.error === 'consent_required') {
        loginWithRedirect();
      }
      throw e;
    }
  } else {
    tokenHeaders = {
      [ACCESS_TOKEN_HEADER]: legacyTokens.accessToken,
      [ID_TOKEN_HEADER]: legacyTokens.idToken,
    };
  }

  const headers = Object.assign(options.headers || {}, tokenHeaders, {
    Accept: '*/*',
    ...(method === 'POST' && !opts.isMultipartUpload
      ? {
          'Content-Type': 'application/json',
        }
      : {}),
  });

  const requestOptions: RequestInit = {
    credentials: 'include',
    headers,
    method,
    ...options,
  };

  const host = options.apiHost ?? `${apiHost()}/api`;

  return fetch(`${host}${uri}`, requestOptions);
};

export const get = (uri: string, opts: RequestOptions = {}): Promise<Response> =>
  request(uri, 'GET', opts);

export const post = (uri: string, opts: RequestOptions = {}): Promise<Response> =>
  request(uri, 'POST', opts);

export const put = (uri: string, opts: RequestOptions = {}): Promise<Response> =>
  request(uri, 'PUT', opts);

// Not naming this delete to avoid colliding with the JS reserved word
export const del = (uri: string, opts: RequestOptions = {}): Promise<Response> =>
  request(uri, 'DELETE', opts);
